// 取り込みファイル (csv / xlsx / xlsm) → 文字列マトリクス。
// 企業インポート (features/company/utiles/excel.ts + ImportCompanyModal) と同じ読み取り方針だが、
// 日付セルを Date として受け取り YYYY/MM/DD に正規化する点が異なる
// (外国人情報は 1号の起算日 / 前回試験の受験日 を日付セルで入力されることが多い)。

import * as XLSX from "xlsx";
import { dayjs } from "@/utils/dayjs";
import {
  getLearnerImportColumns,
  isEmptyExcelRow,
  LEARNER_IMPORT_COLUMNS,
  TLearnerRowExcel,
  validateLearnerFileHeader,
} from "@/features/learner/components/learner-file-import/learner-import-columns";

const DATE_OUTPUT_FORMAT = "YYYY/MM/DD";
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// 日付セル(Date) → YYYY/MM/DD。
// Excel の日付はシリアル値のため、SheetJS が返す Date は 0時ちょうどにならず「前日 23:59:30」のような値になる
// (シリアル値の変換に 30秒 の誤差が入る + タイムゾーン分のずれ)。そのまま format すると 1日前の日付になり、
// 例えば 2024/08/05 と入力されたセルが 2024/08/04 として取り込まれてしまう。
// 誤差はいずれも半日未満のため、最も近い 0時(UTC) へ丸めたうえで UTC のまま日付部分を取り出す
// (ローカル時刻で取り出すと、丸めた分のタイムゾーン差で再びずれる)。
const toDateText = (value: Date): string => {
  const time = value.getTime();
  if (Number.isNaN(time)) return "";
  return dayjs.utc(Math.round(time / MS_PER_DAY) * MS_PER_DAY).format(DATE_OUTPUT_FORMAT);
};

// セル値を文字列へ。日付セル(Date)は YYYY/MM/DD、数値は指数表記にならないよう String() で素直に変換する。
const toCellText = (value: unknown): string => {
  if (value == null) return "";
  if (value instanceof Date) return toDateText(value);
  return String(value).trim();
};

// CSV の文字コードを判定して文字列へ。Excel (Windows) の「CSV」は既定が Shift_JIS (CP932) のため、
// UTF-8 として不正なら Shift_JIS で読み直す (UTF-8 決め打ちだとヘッダが文字化けし「形式不正」になる)。
const decodeCsv = (buffer: ArrayBuffer): string => {
  try {
    // fatal: UTF-8 として壊れているバイト列で例外を投げさせ、Shift_JIS へ切り替える判定にする。
    // 先頭の BOM はヘッダ名に混ざると突合できなくなるため除去する。
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer).replace(/^\uFEFF/, "");
  } catch {
    try {
      return new TextDecoder("shift_jis").decode(buffer);
    } catch {
      // shift_jis 非対応環境では UTF-8 の非 fatal 読みに落とす (文字化けは残るが読み取り自体は継続)。
      return new TextDecoder().decode(buffer);
    }
  }
};

const parseWorkbook = (file: File, data: ArrayBuffer): XLSX.WorkBook => {
  const ext = file.name.split(".").pop()?.toLowerCase();

  if (ext === "csv") {
    return XLSX.read(decodeCsv(data), { type: "string", raw: true, cellText: true });
  }
  if (ext === "xlsx" || ext === "xlsm") {
    // cellDates: 日付書式のセルを Date で受け取り、シリアル値(45292 等)のまま取り込むのを防ぐ。
    return XLSX.read(data, { type: "array", cellDates: true });
  }
  throw new Error("対応していないファイル形式です。csv、xlsx、xlsm のいずれかを選択してください。");
};

// 全キーを空文字で初期化した1行。企業アカウントで存在しない列 (所属機関) も undefined にせず "" に保つ。
const emptyRow = (): TLearnerRowExcel =>
  LEARNER_IMPORT_COLUMNS.reduce((acc, col) => {
    acc[col.key] = "";
    return acc;
  }, {} as TLearnerRowExcel);

// 先頭シートを読み、ヘッダ行を検証してからデータ行を TLearnerRowExcel[] に変換する。
// 列構成はログインユーザ種別で変わる (企業アカウントは所属機関列なし)。
export const readLearnerImportFile = (file: File, isCompanyUser: boolean): Promise<TLearnerRowExcel[]> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => reject(new Error("ファイルの読み込みに失敗しました。"));

    reader.onload = (e) => {
      try {
        const workbook = parseWorkbook(file, e.target?.result as ArrayBuffer);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const matrix: unknown[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "", raw: true, blankrows: false });

        validateLearnerFileHeader(matrix[0] as unknown[], isCompanyUser);

        const columns = getLearnerImportColumns(isCompanyUser);
        const rows: TLearnerRowExcel[] = [];
        for (let i = 1; i < matrix.length; i++) {
          const row = matrix[i];
          if (isEmptyExcelRow(row)) continue;
          rows.push(
            columns.reduce((acc, col, index) => {
              acc[col.key] = toCellText(row[index]);
              return acc;
            }, emptyRow()),
          );
        }

        resolve(rows);
      } catch (err) {
        reject(err);
      }
    };

    // csv も文字コード判定のためバイト列で読む (decodeCsv で UTF-8 / Shift_JIS を切り替える)。
    reader.readAsArrayBuffer(file);
  });
